/**
 * SuperAdminOpsTab.jsx — Ops & Tools: monthly bill checklist, one-tap
 * external tool links (Grafana, Sentry, PostHog, Semgrep, …) and the
 * monthly reminder status/test-send.
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ExternalLink, BellRing, Loader2, CheckCircle2, Activity } from 'lucide-react';
import { BACKEND_URL } from '../config/api';
import { getAdminUserData, canManageTab } from '../utils/superAdmin';

const OPS_API = `${BACKEND_URL}/api/v2/super-admin/ops`;
const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('jwtToken')}` } });

const favicon = (domain) => `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;

const LogoTile = ({ domain, name }) => (
  <div className="w-9 h-9 rounded-lg bg-gray-50 flex items-center justify-center shrink-0 overflow-hidden">
    <img
      src={favicon(domain)}
      alt={name}
      className="w-5 h-5"
      onError={(e) => {
        e.currentTarget.style.display = 'none';
        e.currentTarget.parentElement.textContent = name.charAt(0);
        e.currentTarget.parentElement.className += ' text-sm font-bold text-gray-500';
      }}
    />
  </div>
);

const monthLabel = (ym) => {
  if (!ym) return '';
  const d = new Date(`${ym}-01T00:00:00`);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
};

export default function SuperAdminOpsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendingTest, setSendingTest] = useState(false);
  const userData = getAdminUserData();
  const canManage = canManageTab(userData, 'ops');

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${OPS_API}/overview`, authHeaders());
      setData(res.data);
    } catch {
      toast.error('Could not load Ops overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleBill = async (bill) => {
    if (!canManage) return toast.error('Your role cannot manage Ops');
    const next = !bill.paid;
    setData((d) => ({ ...d, bills: d.bills.map((b) => (b.id === bill.id ? { ...b, paid: next } : b)) }));
    try {
      await axios.post(`${OPS_API}/bills/${bill.id}`, { paid: next }, authHeaders());
    } catch {
      toast.error('Could not save');
      load();
    }
  };

  const sendTest = async () => {
    setSendingTest(true);
    try {
      const res = await axios.post(`${OPS_API}/reminder/test`, {}, authHeaders());
      const r = res.data.results || {};
      toast.success(`Test sent — email: ${r.email?.success ? '✓' : '✗'} · SMS: ${r.sms?.success ? '✓' : '✗'} · WhatsApp: ${r.whatsapp?.success ? '✓' : '✗'}`);
    } catch {
      toast.error('Test reminder failed');
    } finally {
      setSendingTest(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-6 h-6 animate-spin text-gray-400" /></div>;
  }
  if (!data) return null;

  const paidCount = data.bills.filter((b) => b.paid).length;

  return (
    <div className="space-y-8" data-testid="superadmin-ops-tab">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ops &amp; Tools</h1>
        <p className="text-sm text-gray-500 mt-1">Monthly bills, observability and one-tap access to every external tool.</p>
      </div>

      {/* Reminder + OTEL status */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="ops-reminder-card">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 rounded-lg bg-amber-50 flex items-center justify-center shrink-0"><BellRing className="w-4.5 h-4.5 text-amber-600" /></div>
              <div>
                <p className="text-sm font-semibold text-gray-900">Monthly reminder</p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{data.reminder.schedule}</p>
                <p className="text-xs text-gray-400 mt-1">
                  → {data.reminder.email}{data.reminder.phone ? ` · ${data.reminder.phone}` : ''}
                  {data.reminder.lastSentMonth && ` · last sent ${monthLabel(data.reminder.lastSentMonth)}`}
                </p>
              </div>
            </div>
            {canManage && (
              <button
                onClick={sendTest}
                disabled={sendingTest}
                data-testid="ops-send-test-reminder"
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 whitespace-nowrap disabled:opacity-50"
              >
                {sendingTest ? 'Sending…' : 'Send test now'}
              </button>
            )}
          </div>
        </div>
        <div className="bg-white rounded-2xl shadow-sm p-5" data-testid="ops-otel-card">
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-lg bg-emerald-50 flex items-center justify-center shrink-0"><Activity className="w-4.5 h-4.5 text-emerald-600" /></div>
            <div>
              <p className="text-sm font-semibold text-gray-900">Observability pipeline</p>
              <p className="text-xs text-gray-500 mt-0.5">
                OpenTelemetry → Grafana Cloud:{' '}
                <span className={data.observability.otelEnabled ? 'text-emerald-600 font-semibold' : 'text-red-500 font-semibold'}>
                  {data.observability.otelEnabled ? 'exporting' : 'disabled'}
                </span>{' '}· env <span className="font-mono">{data.observability.environment}</span>
              </p>
              <p className="text-xs text-gray-400 mt-1">Traces · metrics · logs shipping straight from the backend.</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bills */}
      <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="ops-bills-card">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Pay the bills — {monthLabel(data.month)}</h2>
            <p className="text-xs text-gray-500 mt-0.5">Check each one off as you pay it. Resets every month.</p>
          </div>
          <span className={`text-sm font-semibold ${paidCount === data.bills.length ? 'text-emerald-600' : 'text-gray-500'}`} data-testid="ops-bills-progress">
            {paidCount}/{data.bills.length} paid{paidCount === data.bills.length ? ' 🎉' : ''}
          </span>
        </div>
        <div className="divide-y divide-gray-50">
          {data.bills.map((bill) => (
            <div key={bill.id} className="flex items-center gap-3 py-3" data-testid={`ops-bill-${bill.id}`}>
              <button
                onClick={() => toggleBill(bill)}
                data-testid={`ops-bill-toggle-${bill.id}`}
                className={`w-5 h-5 rounded-md border flex items-center justify-center transition-colors shrink-0 ${bill.paid ? 'bg-emerald-500 border-emerald-500' : 'border-gray-300 hover:border-gray-400'}`}
                aria-label={`Mark ${bill.name} ${bill.paid ? 'unpaid' : 'paid'}`}
              >
                {bill.paid && <CheckCircle2 className="w-3.5 h-3.5 text-white" />}
              </button>
              <LogoTile domain={bill.domain} name={bill.name} />
              <span className={`text-sm font-medium flex-1 ${bill.paid ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{bill.name}</span>
              <a
                href={bill.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`ops-bill-link-${bill.id}`}
                className="text-xs font-semibold text-blue-600 hover:text-blue-700 flex items-center gap-1"
              >
                Open billing <ExternalLink className="w-3 h-3" />
              </a>
            </div>
          ))}
        </div>
      </div>

      {/* Tools */}
      <div data-testid="ops-tools-section">
        <h2 className="text-base font-semibold text-gray-900 mb-1">One-tap tools</h2>
        <p className="text-xs text-gray-500 mb-4">Graphs, errors, heatmaps, security findings — everything opens in a new tab.</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {data.tools.map((tool) => (
            <a
              key={tool.id}
              href={tool.url}
              target="_blank"
              rel="noreferrer"
              data-testid={`ops-tool-${tool.id}`}
              className="bg-white rounded-2xl shadow-sm p-4 flex items-start gap-3 hover:shadow-md transition-shadow group"
            >
              <LogoTile domain={tool.domain} name={tool.name} />
              <div className="min-w-0">
                <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5">
                  {tool.name}
                  <ExternalLink className="w-3 h-3 text-gray-300 group-hover:text-blue-500 transition-colors shrink-0" />
                </p>
                <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{tool.note}</p>
              </div>
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
